import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  CheckIcon,
  XCircle,
  XIcon,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { cn } from 'lib/utils';
import { Separator } from 'components/ui/separator';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Label } from 'components/ui/label';
import { Popover, PopoverTrigger, PopoverContent } from 'components/ui/popover';
import {
  Command,
  CommandItem,
  CommandGroup,
  CommandList,
  CommandEmpty,
  CommandInput,
  CommandSeparator,
} from 'components/ui/command';
import { FormControl, FormItem, FormMessage } from 'components/ui/form';

const multiSelectVariants = cva(
  'm-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300',
  {
    variants: {
      variant: {
        default:
          'border-foreground/10 text-foreground bg-card hover:bg-card/80',
        secondary:
          'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type MultiSelectOption = {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
};

type SelectedOptionBadgeProps = React.ComponentPropsWithoutRef<typeof Badge> & {
  option: MultiSelectOption;
  toggleOption: (option: string) => void;
  variant?: VariantProps<typeof multiSelectVariants>['variant'];
};

const SelectedOptionBadge = (props: SelectedOptionBadgeProps) => {
  const { option, toggleOption, variant, className, title } = props;

  return (
    <Badge
      title={title}
      className={cn(multiSelectVariants({ variant }), 'truncate', className)}
    >
      {option?.icon && <option.icon className="h-4 w-4 mr-2" />}
      <span className={'truncate'}>{option?.label}</span>
      <XCircle
        className="ml-2 h-4 w-4 cursor-pointer"
        onClick={(event) => {
          event.stopPropagation();
          toggleOption(option.value);
        }}
      />
    </Badge>
  );
};

type ExtraOptionsBadgeProps = React.ComponentPropsWithoutRef<typeof Badge> & {
  children: React.ReactNode;
  clearExtraOptions: () => void;
  variant?: VariantProps<typeof multiSelectVariants>['variant'];
};

const ExtraOptionsBadge = (props: ExtraOptionsBadgeProps) => {
  const { children, clearExtraOptions, variant, title } = props;

  return (
    <Badge
      title={title}
      className={cn(
        'bg-transparent text-foreground border-background/1 hover:bg-transparent',
        multiSelectVariants({ variant })
      )}
    >
      {children}
      <XCircle
        className="ml-2 h-4 w-4 cursor-pointer"
        onClick={(event) => {
          event.stopPropagation();
          clearExtraOptions();
        }}
      />
    </Badge>
  );
};

type MyCommandItemProps = {
  onSelect: () => void;
  children: React.ReactNode;
  selected?: boolean;
};

const MyCommandItem = (props: MyCommandItemProps) => {
  const { onSelect, children, selected } = props;

  return (
    <CommandItem onSelect={onSelect} className="cursor-pointer">
      <div
        className={cn(
          'mr-2 flex h-4 w-4 items-center justify-center border rounded-sm border-primary',
          selected
            ? 'bg-primary text-primary-foreground'
            : 'opacity-50 [&_svg]:invisible'
        )}
      >
        <CheckIcon className="h-4 w-4" />
      </div>
      {children}
    </CommandItem>
  );
};

interface MultiSelectProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'form'>,
    VariantProps<typeof multiSelectVariants> {
  form: UseFormReturn<any, any, undefined>;
  fieldName: string;
  label?: React.ReactNode;
  options: MultiSelectOption[];
  onValueChange?: (value: string[]) => void;
  fieldValue?: string[] | number[];
  placeholder?: string;
  /**
   * Maximum elements to display. Extra selected elements will be summarized.
   * Optional, defaults to 3
   */
  maxCount?: number;
  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;
  className?: string;
  /**
   * CSS classes for inner components
   */
  classNames?: {
    selectedBadge?: string;
  };
}

const MultiSelectGs = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (_props, ref) => {
    const {
      form,
      fieldName,
      label,
      options,
      onValueChange,
      variant,
      fieldValue: _fieldValue = [],
      placeholder = 'Selecione',
      maxCount = 3,
      modalPopover = false,
      classNames,
      className,
      ...props
    } = _props;

    const LabelComponent = React.useMemo(() => {
      if (!label) {
        return <></>;
      } else if (typeof label === 'string') {
        return (
          <Label className="font-GilroySemibold text-sm text-slate-800">
            {label}
          </Label>
        );
      } else {
        return label;
      }
    }, [label]);

    const fieldValue = React.useMemo((): string[] => {
      return _fieldValue.map((option) => {
        if (typeof option === 'number') {
          return option.toString();
        } else {
          return option;
        }
      });
    }, [_fieldValue]);

    const extraOptionsBadgeTitle = React.useMemo(() => {
      if (fieldValue.length > maxCount) {
        const extraOptionValues = fieldValue.slice(maxCount);
        const extraOptionLabels = options
          .filter((option) => extraOptionValues.includes(option.value))
          .map((option) => option.label);
        return extraOptionLabels.join('\r\n');
      } else {
        return undefined;
      }
    }, [options, fieldValue, maxCount]);

    const [popoverOpen, setPopoverOpen] = React.useState(false);

    const updateForm = (value: string[]) => {
      form.setValue(fieldName, value);
      onValueChange?.(value);
    };

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === 'Enter') {
        setPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...fieldValue];
        newSelectedValues.pop();
        updateForm(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = fieldValue.includes(option)
        ? fieldValue.filter((value) => value !== option)
        : [...fieldValue, option];
      updateForm(newSelectedValues);
    };

    const handleClear = () => {
      updateForm([]);
    };

    const handleTogglePopover = () => {
      setPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = fieldValue.slice(0, maxCount);
      updateForm(newSelectedValues);
    };

    const toggleAll = () => {
      if (fieldValue.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        updateForm(allValues);
      }
    };

    return (
      <FormItem>
        <div className="grid w-full items-center gap-1.5">
          {LabelComponent}
          <Popover
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            modal={modalPopover}
          >
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  ref={ref}
                  {...(props as unknown as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                  role="combobox"
                  variant="outline"
                  onClick={handleTogglePopover}
                  className={cn(
                    'flex w-full p-1 font-sans border-solid focus-visible:ring-primary-500 min-h-10 h-auto items-center justify-between',
                    fieldValue.length === 0 && 'text-muted-foreground',
                    className
                  )}
                >
                  {fieldValue.length === 0 && (
                    <div className="flex items-center justify-between w-full mx-auto">
                      <span className="text-sm text-muted-foreground mx-3">
                        {placeholder}
                      </span>
                      <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
                    </div>
                  )}
                  {fieldValue.length > 0 && (
                    <div className="flex justify-between items-center w-full">
                      <div className="flex flex-wrap items-center">
                        {fieldValue.slice(0, maxCount).map((value) => (
                          <SelectedOptionBadge
                            key={value}
                            option={options.find((o) => o.value === value)!}
                            toggleOption={toggleOption}
                            className={classNames?.selectedBadge}
                            title={
                              options.find((o) => o.value === value)?.label
                            }
                          />
                        ))}
                        {fieldValue.length > maxCount && (
                          <ExtraOptionsBadge
                            clearExtraOptions={clearExtraOptions}
                            title={extraOptionsBadgeTitle}
                          >
                            {`+${fieldValue.length - maxCount}`}
                          </ExtraOptionsBadge>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <XIcon
                          className="h-4 mx-2 cursor-pointer text-muted-foreground"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClear();
                          }}
                        />
                        <Separator
                          orientation="vertical"
                          className="flex min-h-6 h-full"
                        />
                        <ChevronsUpDown className="mx-2 cursor-pointer ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </div>
                  )}
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0"
              align="start"
              onEscapeKeyDown={(event) => {
                event.stopPropagation();
                setPopoverOpen(false);
              }}
            >
              <Command
                filter={(value:any, search: string) =>
                  value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                }
              >
                <CommandInput
                  placeholder="Buscar..."
                  onKeyDown={handleInputKeyDown}
                />
                <CommandGroup>
                  <MyCommandItem
                    key="all"
                    onSelect={toggleAll}
                    selected={fieldValue.length === options.length}
                  >
                    Selecionar todos
                  </MyCommandItem>
                </CommandGroup>
                <CommandList>
                  <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
                  <CommandGroup>
                    {options.map((option) => (
                      <MyCommandItem
                        key={option.value}
                        onSelect={() => toggleOption(option.value)}
                        selected={fieldValue.includes(option.value)}
                      >
                        {option.icon && (
                          <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                        )}
                        <span>{option.label}</span>
                      </MyCommandItem>
                    ))}
                  </CommandGroup>
                  {fieldValue.length > 0 && (
                    <>
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem
                          onSelect={handleClear}
                          className="flex-1 justify-center cursor-pointer"
                        >
                          Limpar
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </div>
      </FormItem>
    );
  }
);

MultiSelectGs.displayName = 'MultiSelectGs';

export { MultiSelectGs };
